import React, { useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { api } from '../../../api/client';

interface AdminGuardProps {
  children: ReactNode;
}

/**
 * AdminGuard component - protects admin routes
 * Verifies user is authenticated and has admin privileges
 */
const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyAdmin = async () => {
      if (authLoading) return;

      if (!currentUser) {
        navigate('/');
        return;
      }

      try {
        // Verify admin access with backend
        await api.get('/admin/verify');
        setIsAdmin(true);
      } catch (err) {
        // Not an admin, redirect to home
        console.error('Admin verification failed:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    verifyAdmin();
  }, [currentUser, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return <>{children}</>;
};

export default AdminGuard;
